/**
 * OFListRow - Linha da tabela de OFs (vista lista)
 * Extraído de GestaoOFs.js para melhor modularidade
 */
import React from 'react';
import { Button } from '../../ui/button';
import { Activity, PieChart, Package, Edit, ArrowRight, Trash2, AlertTriangle, Zap, Play, CheckCircle, CheckSquare, Square } from 'lucide-react';

// Configuração de cores de estado
const STATUS_COLORS = {
  rascunho: { bg: 'bg-stone-100', text: 'text-stone-600', icon: null },
  pendente: { bg: 'bg-blue-100', text: 'text-blue-600', icon: null },
  aguardar_entrada: { bg: 'bg-amber-100', text: 'text-amber-600', icon: null },
  em_curso: { bg: 'bg-emerald-100', text: 'text-emerald-600', icon: Play },
  pausado: { bg: 'bg-orange-100', text: 'text-orange-600', icon: null },
  terminado: { bg: 'bg-sky-100', text: 'text-sky-600', icon: CheckCircle },
  cancelado: { bg: 'bg-red-100', text: 'text-red-600', icon: null },
};

const STATUS_LABELS = {
  rascunho: 'Rascunho',
  pendente: 'Ready to Start',
  aguardar_entrada: 'Aguardar Entrada',
  em_curso: 'Em Curso',
  pausado: 'Pausado',
  terminado: 'Terminado',
  cancelado: 'Cancelado',
};

export default function OFListRow({
  of,
  anomalias,
  onOpenAcompanhar,
  onOpenAnaliseTempos,
  onOpenLotes,
  onOpenEdit,
  onOpenStatus,
  onDelete,
  onExportCsv,
  getStatusBadge,
  selectionMode,
  isSelected,
  onToggleSelect,
}) {
  const statusConfig = STATUS_COLORS[of.status] || STATUS_COLORS.pendente;
  const StatusIcon = statusConfig.icon;
  
  return (
    <tr className={`border-t border-stone-100 hover:bg-stone-50 ${isSelected ? 'bg-blue-50' : ''}`} data-testid={`of-row-${of.id}`}>
      {selectionMode && (
        <td className="w-10 py-3 px-2">
          <button onClick={() => onToggleSelect && onToggleSelect(of.id)} className="flex items-center justify-center">
            {isSelected
              ? <CheckSquare className="h-5 w-5 text-blue-600" />
              : <Square className="h-5 w-5 text-stone-400 hover:text-blue-500" />}
          </button>
        </td>
      )}
      <td className="py-3 px-4">
        <div className="flex items-center gap-2">
          <span className="font-bold text-primary font-mono">{of.numero_of}</span>
          {of.urgente && (
            <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-600" title="Urgente">
              <Zap className="h-3 w-3" />
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-4 font-semibold text-stone-900">{of.cliente}</td>
      <td className="py-3 px-4 text-stone-600">{of.artigo}</td>
      <td className="py-3 px-4 text-center">
        <span className="font-semibold text-stone-900">{of.quantidade_total}</span>
        <span className="text-xs text-stone-500 ml-1">un.</span>
      </td>
      <td className="py-3 px-4 text-center">
        <span className="font-semibold text-primary">{of.lotes?.length || 0}</span>
      </td>
      <td className="py-3 px-4">
        <div className="flex items-center justify-center gap-2">
          <div className="w-16 h-2 bg-stone-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full" 
              style={{ width: `${Math.min(of.progresso_real || 0, 100)}%` }}
            />
          </div>
          <span className="text-sm font-semibold">{of.progresso_real || 0}%</span>
        </div>
      </td>
      <td className="py-3 px-4 text-center text-sm">{of.data_entrega ? new Date(of.data_entrega).toLocaleDateString('pt-PT') : '-'}</td>
      <td className="py-3 px-4 text-center">
        <div className="flex flex-col items-center gap-1">
          <button
            onClick={() => onOpenStatus(of)}
            className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1 cursor-pointer hover:opacity-80 transition-opacity ${statusConfig.bg} ${statusConfig.text}`}
            title="Clique para alterar estado"
            data-testid={`status-btn-${of.id}`}
          >
            {StatusIcon && <StatusIcon className="h-3 w-3" />}
            {STATUS_LABELS[of.status] || of.status}
          </button>
          {anomalias?.abertas > 0 && (
            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" />
              {anomalias.abertas}
            </span>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <div className="flex justify-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => onOpenAcompanhar(of)} title="Acompanhar">
            <Activity className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onOpenAnaliseTempos(of)} title="Análise de Tempos" className="text-blue-600 hover:bg-blue-50">
            <PieChart className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onOpenLotes(of)} title="Lotes">
            <Package className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onOpenEdit(of)} title="Editar">
            <Edit className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => onOpenStatus(of)} title="Estado">
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => onDelete(of)} 
            title="Eliminar"
            className="text-red-500 hover:bg-red-50 hover:text-red-600"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </td>
    </tr>
  );
}
